import React, {Fragment} from 'react';
import ReactsCard from "../ui/ReactsCard";
import {useTranslation} from 'react-i18next';
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import {emailRegex} from "../../utils/function-util";
import {StyledSubscriptionAssignmentItem} from './SubscriptionAssignmentItem';
import * as _ from 'lodash';
import ChooseAssignmentMode from "./SubscriptionAssignmentPrompts/ChooseAssignmentMode";
import ChooseMembershipMode from "./SubscriptionAssignmentPrompts/ChooseMembershipMode";
import ChooseOtherSubType from "./SubscriptionAssignmentPrompts/ChooseOtherSubType";
import WarningIcon from '@material-ui/icons/Warning';
import {
    addMember,
    assignAccountAndSubscription,
    assignSubscription,
} from "../../api/organization-unit-functions";
import {SubscriptionAssignmentErrorTypes} from "./SubscriptionAssignmentPrompts/subscriptionAssignmentErrorTypes";
import GenericAssignmentError from "./SubscriptionAssignmentPrompts/GenericAssignmentError";
import CSVImport from "../_shared/CSVImport";
import {useNotification} from "../ui/notification/NotificationProvider";
import {FormStatus, KeyValue} from "../../models/shared";
import {MembershipUserRowData} from "../../models/membership.models";
import {SubscriptionCount, SubscriptionType} from "../../models/subscription-model";
import withEditorComponentStyle from "../ui/styles/StyledEditorComponent";
import i18n from "i18next";
import {SupportedLanguages} from "../_shared/LanguageSelector";

export const AssignmentModes = {
    SubscriptionAndAccount: "SubscriptionAndAccount",
    SubscriptionOnly: "SubscriptionOnly"
};

export const MembershipModes = {
    MembershipAndSubscription: "MembershipAndSubscription",
    MembershipAndForceSubscription: "MembershipAndForceSubscription",
    MembershipOnly: "MembershipOnly"
};

const assignableSubscriptionTypes = [SubscriptionType.Standard, SubscriptionType.Provider15];

const CSVFormat = {
    headers: ['SubscriptionType', 'Language', 'Email', 'FirstName', 'LastName'],
    examples: [
        ['Pro', 'en', 'js.smith@gmail.com', 'John', 'Smith'],
        ['Standard', 'fr', 'j.doe@gmail.com', 'James', 'Doe']
    ]
};

const getSubscriptionType = (data: any) => {
    const subType = data.toLowerCase().trim();
    if (subType === 'pro' || subType === 'provider')
        return SubscriptionType.Provider15;

    if (subType === 'stand' || subType === 'standard')
        return SubscriptionType.Standard;

    return undefined;
};

const getLanguage = (data: any) => {
    const supportedLanguages = SupportedLanguages(x => x).map(x => x.code);
    const trimmedData = data.toLowerCase().trim();
    if (supportedLanguages.filter(x => x == trimmedData).length === 1) {
        return trimmedData;
    } else {
        return undefined;
    }
};

const buildItemData = (data: any, availableTypes: SubscriptionType[]): MembershipUserRowData => {
    if (!data || !data.length) {
        return {subscriptionType: availableTypes[0], email: '', firstName: '', lastName: '', language: i18n.language};
    }

    let subscriptionType = getSubscriptionType(data[0]);
    let language = getLanguage(data[1]);

    return {
        subscriptionType: subscriptionType,
        email: data[2],
        firstName: data[3],
        lastName: data[4],
        language: language ?? i18n.language
    }
};

const buildItem = (availableTypes: SubscriptionType[], mode?: any, data: any[] = []) => ({
    id: Math.round(Math.random() * 100000),
    data: buildItemData(data, availableTypes),
    availableTypes: availableTypes,
    errors: {},
    status: FormStatus.none,
    correctiveAction: null,
    mode: mode,
    membershipMode: MembershipModes.MembershipAndSubscription
});

const SubscriptionAssignmentEditor = (
    {
        organizationId,
        organizationUnitId,
        subscriptionCounts,
        onDone,
        classes
    }: {
        organizationId: number,
        organizationUnitId: string,
        subscriptionCounts: SubscriptionCount[]
        onDone: () => void,
        classes: any
    }) => {

    const subscriptionTypeAssignedCount = (type: SubscriptionType): number => forms.reduce((acc, item) => item.data.subscriptionType === type ? ++acc : acc, 0);

    const remainingAvailableSubscriptionTypes = (initialCount: boolean = false): SubscriptionType[] => {
        return subscriptionCounts.length > 0 ?
            initialCount ? assignableSubscriptionTypes.filter(type => subscriptionCounts.some(sc => sc.subscriptionType === type && sc.availableSubscriptions > 0))
                : assignableSubscriptionTypes.filter(type => subscriptionTypeAssignedCount(type) < subscriptionCounts.filter(sc => sc.subscriptionType === type)[0].availableSubscriptions)
            : [];
    }
    const {t, i18n} = useTranslation();
    const [forms, setForms] = React.useState([buildItem(remainingAvailableSubscriptionTypes(true), AssignmentModes.SubscriptionAndAccount)]);
    const [submitted, setSubmitted] = React.useState(false);
    const title = 'Subscription.SubscriptionAssignment.Title';
    const processing = forms.some(f => f.status === FormStatus.processing);
    const failedCount = forms.filter(f => f.status === FormStatus.failed).length;
    const allSuccessful = forms.length > 0 && forms.every(f => f.status === FormStatus.successful);
    const {pushNotification} = useNotification();


    const assignSubOnlyPrompt = (id: any) => <ChooseAssignmentMode className={classes.errorOption}
                                                                   onConfirm={() => handleAssignSubOnly(id)}
                                                                   onCancel={() => handleFormDeleted(id)}/>;
    const forceSubPrompt = (id: any) => <ChooseMembershipMode className={classes.errorOption}
                                                              onConfirm={(membershipMode: any) => handleMembershipMode(id, membershipMode)}
                                                              onCancel={() => handleFormDeleted(id)}/>;
    const chooseOtherSubType = (id: any) => <ChooseOtherSubType className={classes.errorOption}
                                                                onCancel={() => handleFormDeleted(id)}/>;
    const failedToAssignSub = (id: any, msg: any) => <GenericAssignmentError message={msg}
                                                                             className={classes.errorOption}
                                                                             onCancel={() => handleFormDeleted(id)}/>;

    const canAdd = (): boolean => {
        return remainingAvailableSubscriptionTypes().length > 0;
    }

    const addHandler = () => {
        setForms(newForms => ([...newForms, buildItem(remainingAvailableSubscriptionTypes(), AssignmentModes.SubscriptionAndAccount)]))
    };

    const resetHandler = async (e: any) => {
        setForms([{...buildItem(remainingAvailableSubscriptionTypes(true), AssignmentModes.SubscriptionAndAccount)}]);
        setSubmitted(false);
    };

    const submitForms = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setSubmitted(true);

        const validatedForms = forms.map(f => {
            const errors = ['email', 'firstName', 'lastName', 'subscriptionType', 'language']
                .reduce((acc, name) => ({...acc, ...validateField(name, (f.data as any)[name])}), {});
            return {
                ...f,
                errors
            }
        });

        if (validatedForms.some(f => _.values(f.errors).some((v: any) => !!v)))
            return setForms(validatedForms);

        const formsToProcess = forms.filter(f => f.status !== FormStatus.successful && f.status !== FormStatus.processing);

        for (let i = 0; i < formsToProcess.length; i++) {
            await submitForm(formsToProcess[i].id);
        }
    };

    const submitForm = async (id: any) => {
        const userForm = forms.find(f => f.id === id);

        if (!userForm) return;

        const addMemberWithSubscription = async () => await assignSubscription(organizationId, parseInt(organizationUnitId), {
            ...userForm.data,
            subscriptionType: userForm.data.subscriptionType!,
            forceAssign: userForm.membershipMode === MembershipModes.MembershipAndForceSubscription
        });

        const addMemberWithAccountAndSubscription = async () => await assignAccountAndSubscription(organizationId, parseInt(organizationUnitId), {
            ...userForm.data,
            subscriptionType: userForm.data.subscriptionType!
        });

        const addMemberOnly = async () => await addMember(organizationId, parseInt(organizationUnitId), {
            ...userForm.data,
            subscriptionType: userForm.data.subscriptionType!
        });

        updateForm(id, {status: FormStatus.processing});
        try {
            if (userForm.mode === AssignmentModes.SubscriptionAndAccount) {
                await addMemberWithAccountAndSubscription();
            } else {
                if (userForm.membershipMode === MembershipModes.MembershipOnly)
                    await addMemberOnly();
                else
                    await addMemberWithSubscription();
            }

            updateForm(id, {status: FormStatus.successful, correctiveAction: null});
        } catch (err) {
            const errorType: any = _.get(err, 'response.data.error.type');

            let correctiveAction = null;
            switch (errorType) {
                case SubscriptionAssignmentErrorTypes.UserAlreadyHasSubscriptionOfTypeFromOrganization:
                    correctiveAction = forceSubPrompt(id);
                    break;
                case SubscriptionAssignmentErrorTypes.NoAvailableSubscriptionForType:
                    correctiveAction = chooseOtherSubType(id);
                    break;
                case SubscriptionAssignmentErrorTypes.UserAccountAlreadyExist:
                    correctiveAction = assignSubOnlyPrompt(id);
                    break;
                default: {
                    const msg = errorType ? `_Common.Api.Messages.${errorType}` : `Subscription.SubscriptionAssignment.FailedToAssignSubscriptionToUser`;
                    correctiveAction = failedToAssignSub(id, msg);
                }
            }
            updateForm(id, {status: FormStatus.failed, correctiveAction});
        }
    };

    const updateForm = (id: any, patch: any) => {
        setForms(newForms => {
            const index = newForms.findIndex(f => f.id === id);
            if (index <= -1) return newForms;
            const result = [...newForms];
            result[index] = _.merge({}, newForms[index], patch);
            return result;
        });
    };

    const handleAssignSubOnly = (id: any) => {
        updateForm(id, {
            mode: AssignmentModes.SubscriptionOnly,
            correctiveAction: null,
            status: FormStatus.none
        });
    };

    const handleMembershipMode = (id: any, membershipMode: any) => {
        updateForm(id, {
            membershipMode: membershipMode,
            correctiveAction: null,
            status: FormStatus.none
        });
    };

    const handleFormChange = (name: any, value: any, form: any) => {
        const errors = validateField(name, value);
        const update = {
            data: {[name]: value},
            errors: errors
        };

        if (name === 'subscriptionType') {
            updateFormSubscriptionTypeError(value, 1);
            assignableSubscriptionTypes.forEach(type => {
                if (type !== value)
                    updateFormSubscriptionTypeError(type, -1);
            })
        }

        updateForm(form.id, update);
    };

    const handleFormDeleted = (id: any) => {
        if (forms.some(f => f.id === id && _.values(f.errors).some(v => v === 'NoSubscription'))) {
            const type = forms[forms.findIndex(f => f.id === id)]?.data?.subscriptionType;
            if (!!type)
                updateFormSubscriptionTypeError(type, -1);
        }
        setForms(formsToDelete => {
            const index = formsToDelete.findIndex(f => f.id === id);
            if (index <= -1) return formsToDelete;
            const newForms = [...formsToDelete];
            newForms.splice(index, 1);
            return newForms;
        });
    };

    const updateFormSubscriptionTypeError = (changedType: SubscriptionType, offset: number) => {
        if (subscriptionTypeAssignedCount(changedType) + offset > subscriptionCounts.filter(sc => sc.subscriptionType === changedType)[0].availableSubscriptions) {
            forms.forEach((form) => {
                if (form.data.subscriptionType === changedType) {
                    const update = {
                        errors: {['subscriptionType']: 'NoSubscription'},
                    };
                    updateForm(form.id, update);
                }
            })
        } else {
            forms.forEach((form) => {
                if (form.data.subscriptionType === changedType) {
                    const update = {
                        errors: {['subscriptionType']: null},
                    };
                    updateForm(form.id, update);
                }
            })
        }
    }

    const validateField = (field: any, value: any) => {
        const errors: KeyValue<string> = {};
        switch (field) {
            case 'email':
                errors[field] = emailRegex.test(value) ? null : 'EmailIsInvalid';
                break;
            case 'subscriptionType':
                errors[field] = subscriptionTypeAssignedCount(value) <= subscriptionCounts.filter(sc => sc.subscriptionType === value)[0].availableSubscriptions ? null : 'NoSubscription';
                break;
            default:
                errors[field] = !value ? 'FieldIsRequired' : null;
                break;
        }
        return errors;
    };

    const loadCSVData = (data: any) => {
        setForms(data.map((row: any) => buildItem(assignableSubscriptionTypes, AssignmentModes.SubscriptionAndAccount, row)));
    };

    const onLoadCSVError = (errorMsg: any, errorCode: any) => {
        const errorCodeMsg = errorCode ? '\nError code: ' + errorCode : '';
        if (errorMsg) {
            pushNotification({
                message: errorMsg + errorCodeMsg,
                severity: 'warning'
            });
        }
    };

    return <ReactsCard title={t(title)}>
        {
            failedCount > 0 ?
                <Grid container className={classes.errorBanner} justify='center'>
                    <WarningIcon/>
                    <Typography variant="body2">
                        {t('Subscription.SubscriptionAssignment.XEntriesNeedAttention', {failedCount})}
                    </Typography>
                </Grid>
                : <Typography variant="h6">{t('Subscription.SubscriptionAssignment.Description')}</Typography>

        }
        {subscriptionCounts.some(sc => sc.availableSubscriptions > 0) ?
            <Fragment>
                <Grid container className={classes.formsContainer}>
                    {
                        forms.map((f, i) => !!f.availableTypes.length && <StyledSubscriptionAssignmentItem
                                form={f}
                                key={f.id.toString()}
                                handleFormDeleted={handleFormDeleted}
                                handleFormChange={handleFormChange}
                                submitted={submitted}>
                                {f.correctiveAction}
                            </StyledSubscriptionAssignmentItem>
                        )
                    }
                </Grid>
                <Grid container justify="flex-start">
                    <IconButton aria-label="add" className={classes.addBtn} onClick={addHandler} disabled={!canAdd()}>
                        <AddIcon/>
                    </IconButton>
                </Grid>
                <Grid container justify="flex-end" className={classes.actions}>
                    {
                        allSuccessful ?
                            <Button variant="outlined" color="primary" size="medium"
                                    onClick={onDone}>
                                {t('_Common.Labels.Done')}
                            </Button> :
                            <Fragment>
                                <Button variant="outlined" color="primary" size="medium"
                                        onClick={onDone}
                                        disabled={processing}>
                                    {t('_Common.Labels.Cancel')}
                                </Button>
                                <Button variant="outlined" color="primary" size="medium"
                                        onClick={resetHandler}
                                        disabled={processing}>
                                    {t('_Common.Labels.Reset')}
                                </Button>
                                <Button variant="contained" color="primary" size="medium"
                                        onClick={submitForms}
                                        disabled={processing}>
                                    {t('_Common.Labels.Apply')}
                                </Button>
                            </Fragment>
                    }
                </Grid>
                <CSVImport displayedLabel={t('_Common.Labels.CSV')}
                           format={CSVFormat}
                           onComplete={loadCSVData}
                           onError={onLoadCSVError}/>
            </Fragment> :
            <div>{t('Subscription.SubscriptionAssignment.NoAvailableSubscriptions')}</div>
        }
    </ReactsCard>
};

export const StyledSubscriptionAssignementEditor =  withEditorComponentStyle(SubscriptionAssignmentEditor);
